import {
	ConfirmationDialog,
	ContextMenu,
	ContextMenuItem,
	IDataListColumn,
	PaginatedAutoDataList,
	laravelPaginatedListFetchConfig,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Address } from "../../../shared/ui/Address";
import { birthdayWidgetService } from "../dashboard/widgets/birthdays/service";
import { companyService } from "../shared/api/companyService";
import { TenantArchiveDrawer } from "../shared/tenantArchive/TenantArchiveDrawer";
import { useRestoreByTenantHandler } from "../shared/tenantArchive/useRestoreByTenantHandler";
import { ICompanyIndexResource } from "../shared/types";

export const ArchivedCompanyList = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const {
		clickHandler: restoreClickHandler,
		drawerProps,
		confirmationProps,
	} = useRestoreByTenantHandler(companyService, () =>
		queryClient.invalidateQueries({ queryKey: birthdayWidgetService.key }),
	);
	const dataListColumns: IDataListColumn<ICompanyIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
				sortable: true,
			},
			{
				heading: t("Address"),
				valueKey: "primaryAddress",
				type: "value",
				render: (company) => (company.primaryAddress ? <Address address={company.primaryAddress} /> : "-"),
			},
			{
				heading: t("Tags"),
				valueKey: "tags",
				type: "value",
				render: (company) => company.tags.map((tag) => tag.name).join(", "),
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							action={() => restoreClickHandler(id)}
							title={t("Restore")}
							label={t("Restore")}
							icon="unarchive"
							shortcut="primary"
						/>
					</ContextMenu>
				),
			},
		],
		[restoreClickHandler, t],
	);

	return (
		<>
			<PaginatedAutoDataList
				name="archivedCompanies"
				columns={dataListColumns}
				onRowClick={({ id }) => navigate(`${id}`)}
				initialSearch={{ search: "", searchFields: [{ name: "name", label: t("Name") }] }}
				empty={t("No archived companies")}
				listFetchConfig={laravelPaginatedListFetchConfig(companyService.indexArchivedKey, companyService.indexArchived)}
				heading={t("Archived companies")}
			/>
			<TenantArchiveDrawer {...drawerProps} title={t("Restore")} type="restore" service={companyService} />
			<ConfirmationDialog
				{...confirmationProps}
				heading={t("Restore")}
				labels={{ close: t("Close"), confirm: t("Confirm") }}
			>
				{t("Are you sure you want to restore this company?")}
			</ConfirmationDialog>
		</>
	);
};
